/**
 * Submenú de **Administración** (Gestión).
 *
 * El módulo `gestion.administracion` abre directamente los **comprobantes**; las
 * otras pantallas del antiguo centro de administración (canjes masivos de notas,
 * consumo por cliente, el mapa de calor de surtidores, la cotización y la
 * conectividad con Hermes) se alcanzan con este submenú, igual que Inventario
 * tiene el suyo.
 */

import { Calculator, Cog, Flame, HandCoins, Link2, ReceiptText, Ticket, UserSearch, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

export type AdministracionSeccion =
  | 'comprobantes'
  | 'canjes'
  | 'boletaje'
  | 'generador'
  | 'consumo'
  | 'calor'
  | 'cotizacion'
  | 'hermes';

interface Enlace {
  seccion: AdministracionSeccion;
  to: string;
  label: string;
  icono: LucideIcon;
}

/** Orden del submenú: los comprobantes primero por ser la pantalla principal. */
const ENLACES: readonly Enlace[] = [
  { seccion: 'comprobantes', to: '/gestion/administracion/comprobantes', label: 'Comprobantes', icono: ReceiptText },
  { seccion: 'canjes', to: '/gestion/administracion/canjes-notas', label: 'Canjes de notas', icono: HandCoins },
  { seccion: 'boletaje', to: '/gestion/administracion/boletaje-masivo', label: 'Boletaje masivo', icono: Ticket },
  { seccion: 'generador', to: '/gestion/administracion/generador-masivo', label: 'Generador masivo', icono: Cog },
  { seccion: 'consumo', to: '/gestion/administracion/consumo', label: 'Consumo por cliente', icono: UserSearch },
  { seccion: 'calor', to: '/gestion/administracion', label: 'Mapa de calor', icono: Flame },
  { seccion: 'cotizacion', to: '/gestion/cotizaciones', label: 'Cotización', icono: Calculator },
  { seccion: 'hermes', to: '/gestion/administracion/hermes', label: 'Conectividad Hermes', icono: Link2 },
];

const BASE =
  'inline-flex shrink-0 items-center gap-2 rounded-control px-3 py-2 text-xs font-semibold transition-colors';
const INACTIVO = 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700';
const ACTIVO = 'bg-ink-900 text-white';

interface AdministracionNavProps {
  /** Sección actualmente visible; se resalta en la barra. */
  active: AdministracionSeccion;
  className?: string;
}

export function AdministracionNav({ active, className }: AdministracionNavProps) {
  return (
    <nav
      className={cn('flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0', className)}
      aria-label="Secciones de administración"
    >
      {ENLACES.map((enlace) => (
        <Link
          key={enlace.seccion}
          to={enlace.to}
          aria-current={enlace.seccion === active ? 'page' : undefined}
          className={cn(BASE, enlace.seccion === active ? ACTIVO : INACTIVO)}
        >
          <enlace.icono className="size-3.5" aria-hidden="true" />
          {enlace.label}
        </Link>
      ))}
    </nav>
  );
}
